import React, { useState, useEffect } from "react";
import {
  Box,
  TextField,
  Button,
  MenuItem,
  Typography,
  CircularProgress,
} from "@mui/material";
import apiClient from "../utils/apiClient";

const FIELD_LABELS = {
  plot_uuid: "Bed",
  planted_uuid: "Crop",
  event_type_uuid: "Event type",
  window_start: "From date",
  window_end: "To date",
  date_executed: "Execution date",
  amount: "Amount",
  output: "Yield (kg)",
  notes: "Notes",
  status: "Status",
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toISOString().split("T")[0];
};

const EventForm = ({
  garden_uuid,
  plots = [],
  plantedCrops = [],
  onSubmit,
  onCancel,
  onDelete,
  initialData = null,
}) => {
  const [eventTypes, setEventTypes] = useState([]);
  const [selectedEventType, setSelectedEventType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [errors, setErrors] = useState({});

  const [formData, setFormData] = useState({
    plot_uuid: "",
    planted_uuid: "",
    event_type_uuid: "",
    window_start: "",
    window_end: "",
    date_executed: "",
    amount: "",
    output: "",
    notes: "",
    status: "planned",
  });

  const canonicalizeFieldKey = (key) => {
    if (!key) return key;
    return String(key).trim().toLowerCase();
  };

  useEffect(() => {
    const fetchEventTypes = async () => {
      try {
        const res = await apiClient.get("/eventtypes");
        setEventTypes(res.data || []);
      } catch (err) {
        console.error("Failed to load event types:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchEventTypes();
  }, []);

  useEffect(() => {
    if (initialData) {
      const canonicalInitial = Object.fromEntries(
        Object.entries(initialData).map(([k, v]) => [canonicalizeFieldKey(k), v])
      );
      const normalized = {
        ...canonicalInitial,
        window_start: formatDate(canonicalInitial.window_start),
        window_end: formatDate(canonicalInitial.window_end),
        date_executed: formatDate(canonicalInitial.date_executed),
      };
      setFormData((prev) => ({ ...prev, ...normalized }));
      const existingType = eventTypes.find(
        (t) => t.event_type_uuid === initialData.event_type_uuid
      );
      setSelectedEventType(existingType || null);
    }
  }, [initialData, eventTypes]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const canonicalName = canonicalizeFieldKey(name);
    setFormData((prev) => ({ ...prev, [canonicalName]: value }));
  };

  const handleEventTypeChange = (e) => {
    const uuid = e.target.value;
    const type = eventTypes.find((t) => t.event_type_uuid === uuid);
    setSelectedEventType(type);
    setFormData((prev) => ({ ...prev, event_type_uuid: uuid }));
  };

  const isPlanned = formData.status === "planned";
  const isDone = formData.status === "done";
  const isSkipped = formData.status === "skipped";
  const dynamicFieldsRaw = selectedEventType?.input_fields || [];
  const dynamicFields = Array.from(
    new Set(dynamicFieldsRaw.map(canonicalizeFieldKey).filter(Boolean))
  );
  const showExecutionDate = isDone && dynamicFields.includes("date_executed");
  const showWindowDates = isPlanned || isSkipped;

  const validateForm = () => {
    const newErrors = {};

    if (showWindowDates) {
      if (!formData.window_start)
        newErrors.window_start = "From date is required";
      if (!formData.window_end)
        newErrors.window_end = "To date is required";
    }

    if (showExecutionDate && !formData.date_executed)
      newErrors.date_executed = "Execution date is required";

    dynamicFields.forEach((field) => {
      if (field === "notes") return;
      if (
        (showExecutionDate || field !== "date_executed") &&
        !formData[field]
      ) {
        newErrors[field] = `${FIELD_LABELS[field] || field} is required`;
      }
    });

    if (!formData.plot_uuid)
      newErrors.plot_uuid = "Bed must be selected";
    if (!formData.event_type_uuid)
      newErrors.event_type_uuid = "Event type is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    const isValid = validateForm();
    const normalized = Object.fromEntries(
      Object.entries(formData).map(([k, v]) => [k, v === "" ? null : v])
    );
    if (!isValid) return;
    onSubmit(normalized);
  };

  const eventName = selectedEventType?.name?.toLowerCase() || "";

  const getTargetAmount = () => {
    const planted =
      plantedCrops.find((p) => p.planted_uuid === formData.planted_uuid) ||
      initialData?.planted ||
      null;
    return planted?.amount ?? null;
  };

  const getAmountHelper = () => {
    const target = getTargetAmount();
    const targetSuffix = target != null ? ` (target: ${target})` : "";
    if (eventName.includes("høst") || eventName.includes("harvest"))
      return `Number of plants harvested${targetSuffix}`;
    if (eventName.includes("såning") || eventName.includes("sowing"))
      return `Number of seeds sown${targetSuffix}`;
    if (eventName.includes("udplantning") || eventName.includes("plant"))
      return `Number of seedlings planted${targetSuffix}`;
    if (eventName.includes("forspiring") || eventName.includes("germination"))
      return `Number of seeds pre-germinated${targetSuffix}`;
    return `Number of plants, rows, or seeds${targetSuffix}`;
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={2}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        {initialData ? "Edit task" : "Add new task"}
      </Typography>

      <TextField
        select
        fullWidth
        label={FIELD_LABELS.status}
        name="status"
        value={formData.status}
        onChange={handleChange}
        margin="normal"
      >
        {["planned", "done", "skipped"].map((s) => (
          <MenuItem key={s} value={s}>
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        fullWidth
        label={FIELD_LABELS.plot_uuid}
        name="plot_uuid"
        value={formData.plot_uuid}
        onChange={handleChange}
        margin="normal"
        error={!!errors.plot_uuid}
        helperText={errors.plot_uuid}
      >
        <MenuItem value="">Select bed</MenuItem>
        {plots.map((plot) => (
          <MenuItem key={plot.plot_uuid} value={plot.plot_uuid}>
            {plot.name}
          </MenuItem>
        ))}
      </TextField>

      <TextField
        select
        fullWidth
        label={FIELD_LABELS.planted_uuid}
        name="planted_uuid"
        value={formData.planted_uuid}
        onChange={handleChange}
        margin="normal"
        disabled={!formData.plot_uuid}
      >
        <MenuItem value="">Select crop</MenuItem>
        {plantedCrops
          .filter((p) => p.plot_uuid === formData.plot_uuid)
          .map((planted) => (
            <MenuItem key={planted.planted_uuid} value={planted.planted_uuid}>
              {planted.crop?.name || "Unnamed crop"}
            </MenuItem>
          ))}
      </TextField>

      <TextField
        select
        fullWidth
        label={FIELD_LABELS.event_type_uuid}
        name="event_type_uuid"
        value={formData.event_type_uuid}
        onChange={handleEventTypeChange}
        margin="normal"
        error={!!errors.event_type_uuid}
        helperText={errors.event_type_uuid}
      >
        <MenuItem value="">Select event type</MenuItem>
        {eventTypes.map((et) => (
          <MenuItem key={et.event_type_uuid} value={et.event_type_uuid}>
            {et.name}
          </MenuItem>
        ))}
      </TextField>

      {showWindowDates && (
        <>
          <TextField
            fullWidth
            type="date"
            label="From date"
            name="window_start"
            value={formData.window_start}
            onChange={handleChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={!!errors.window_start}
            helperText={errors.window_start}
          />
          <TextField
            fullWidth
            type="date"
            label="To date"
            name="window_end"
            value={formData.window_end}
            onChange={handleChange}
            margin="normal"
            InputLabelProps={{ shrink: true }}
            error={!!errors.window_end}
            helperText={errors.window_end}
          />
        </>
      )}

      {dynamicFields.map((fieldKey) => {
        if (fieldKey === "date_executed" && !showExecutionDate) return null;
        const label = FIELD_LABELS[fieldKey] || fieldKey;
        const isNumber = ["amount", "output"].includes(fieldKey);
        const isDate = fieldKey === "date_executed";
        const multiline = fieldKey === "notes";
        const helperText =
          errors[fieldKey] ||
          (fieldKey === "amount" ? getAmountHelper() : undefined);

        if (fieldKey === "notes") {
          return (
            <Box key={fieldKey} sx={{ mt: 2 }}>
              <Typography
                variant="subtitle2"
                sx={{ mb: 0.5, fontWeight: 500 }}
              >
                {label}
              </Typography>
              <TextField
                fullWidth
                name={fieldKey}
                type="text"
                value={formData[fieldKey] || ""}
                onChange={handleChange}
                margin="normal"
                multiline
                minRows={2}
                helperText={helperText}
                error={!!errors[fieldKey]}
              />
            </Box>
          );
        }

        return (
          <TextField
            key={fieldKey}
            fullWidth
            label={label}
            name={fieldKey}
            type={isDate ? "date" : isNumber ? "number" : "text"}
            value={formData[fieldKey] || ""}
            onChange={handleChange}
            margin="normal"
            InputLabelProps={{ shrink: isDate || isNumber }}
            multiline={multiline}
            minRows={multiline ? 2 : 1}
            helperText={helperText}
            error={!!errors[fieldKey]}
          />
        );
      })}

      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 3, gap: 2 }}>
        <Box sx={{ display: "flex", gap: 1 }}>
          {onDelete && (
            <Button 
              onClick={onDelete}
              color="secondary"
              variant="outlined"
              className="vintage-button"
            >
              Delete
            </Button>
          )}
        </Box>
        <Box sx={{ display: "flex", gap: 1 }}>
          <Button 
            onClick={onCancel}
            variant="outlined"
            className="vintage-button"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="contained"
            color="primary"
            onClick={handleSubmit}
            disabled={!formData.event_type_uuid}
            className="vintage-button"
          >
            {initialData ? "Update" : "Save"}
          </Button>
        </Box>
      </Box>
    </Box>
  );
};

export default EventForm;
